import { filter, reduce, uniqBy } from 'lodash';

function sumNumericFieldsInto(acc, curr, excludeKeys) {
    Object.entries(curr).forEach(([k, v]) => {
        if (k === 'am_accountNo' || excludeKeys.includes(k)) return;
        acc[k] = acc[k] || 0;
        if (v !== '' && !isNaN(v)) acc[k] += +v;
    });
}

// Builds the level1/level2/level3 field mapping, filterable header list, and chart metric
// options from a report's column config — the same derivation collection-dashboard does per
// selected report, but as a pure function so each dashboard card can call it independently.
export function buildHeaderMeta(columnsList) {
    const level1 = filter(columnsList, el => el.summary_level === 'Level 1')[0]?.field_key;
    const level2 = filter(columnsList, el => el.summary_level === 'Level 2')[0]?.field_key;
    const level3 = filter(columnsList, el => el.summary_level === 'Level 3')[0]?.field_key;

    const tableHeader = filter(columnsList, el =>
        el.field_key === level1 || el.field_key === level2 || el.field_key === level3 || el.data_type === 'number'
    );
    tableHeader.forEach(el => { el.Header = el.label; el.accessor = el.field_key; });

    const percentObject = filter(columnsList, el => Object.keys(el).includes('percentageFields'))
        .map(el => ({ header: el.field_key, value: el.percentageFields }));

    const chartDropdown = filter(tableHeader, el => el.data_type === 'number')
        .map(el => ({ key: el.field_key, value: el.Header }));

    return { level1, level2, level3, tableHeader, percentObject, chartDropdown };
}

// Same ruleCondition derivation as collection-dashboard's reportHeader onSuccess handler,
// parameterized instead of reading module-level entity/product/team/branch variables.
export function buildRuleCondition(variables, { level1, level2, entity, product, team, branch }) {
    const ruleCondition = [];
    uniqBy(variables, 'id').forEach(el => {
        if (!el.value?.includes?.('{')) {
            ruleCondition.push(el);
            return;
        }
        if (['am_associatedBranch', 'am_associatedDealer'].includes(el.id)) ruleCondition.push({ id: el.id, value: branch });
        if (el.id === level1) ruleCondition.push({ id: el.id, value: entity });
        if (el.id === level2) ruleCondition.push({ id: el.id, value: product });
        if (el.id === 'amt_team') ruleCondition.push({ id: el.id, value: team });
    });
    return ruleCondition;
}

function applyPercentFields(row, percentObject) {
    let result = { ...row };
    percentObject.forEach(el => {
        if (!row.hasOwnProperty(el.header)) return;
        result = el.header === 'delinquentPercentage'
            ? { ...result, [el.header]: Math.round(reduce(el.value[0], (sum, key) => sum + row[key], 0) / row[el.value[1]] * 100) || 0 }
            : { ...result, [el.header]: Math.round((row[el.value[0]] / row[el.value[1]]) * 100) || 0 };
    });
    return result;
}

// Same two-tier (level1 totals, level2 breakdown per level1) aggregation math as
// collection-dashboard's formReportData — the level3 tier is intentionally dropped: it only
// ever fed the table's third expandable row level, which this chart-only dashboard has no
// use for, and UIChart itself never reads it.
export function aggregateReportData(rawRows, { level1, level2, percentObject }) {
    if (!rawRows?.length || !level1) return [];

    // One entry per level1 value, carrying both its own summed numeric fields (for the Bar
    // chart) and its raw member rows (grouped into level2 buckets below, for the Pie chart).
    const lvl1Groups = Object.values(rawRows.reduce((acc, curr) => {
        const id = curr[level1];
        acc[id] = acc[id] || { [level1]: id, subRows: [] };
        acc[id].subRows.push(curr);
        sumNumericFieldsInto(acc[id], curr, [level1, level2]);
        return acc;
    }, {}));

    const withLevel2Breakdown = lvl1Groups.map(group => {
        const lvl2Buckets = Object.values(group.subRows.reduce((acc, curr) => {
            const key = curr[level2];
            acc[key] = acc[key] || { [level1]: curr[level1], [level2]: key };
            sumNumericFieldsInto(acc[key], curr, [level1, level2]);
            return acc;
        }, {}));
        return { ...group, subRows: lvl2Buckets };
    });

    const percentLvl1 = withLevel2Breakdown.map(el => applyPercentFields(el, percentObject));
    return percentLvl1.map(el => ({ ...el, subRows: el.subRows.map(sub => applyPercentFields(sub, percentObject)) }));
}

export function amountFormat(value) {
    return !value ? '0' : parseFloat(value?.toFixed(2))?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Same flattening/relabeling collection-dashboard's formDownloadData does, limited to the two
// tiers aggregateReportData produces (level1 totals + level2 breakdown rows) since there's no
// level3 account-detail tier to flatten here.
export function buildDownloadRows(percentLvl2, tableHeader, { level1, level2 }) {
    const rows = [];
    percentLvl2.forEach(el => {
        rows.push({ ...el, [level2]: '', subRows: undefined });
        el.subRows.forEach(sub => rows.push({ ...sub, [level1]: el[level1] }));
    });
    const flatRows = rows.map(({ subRows, ...rest }) => rest);

    const headerFields = filter(tableHeader, h => h.field_key === level1 || h.field_key === level2 || h.data_type === 'number');
    const headerMap = headerFields.reduce((acc, { label, field_key }) => { acc[field_key] = label; return acc; }, {});
    const headingMap = { [level1]: null, [level2]: null, ...headerMap };

    return flatRows.map(row => Object.fromEntries(
        Object.keys(headingMap).map((key, index) => {
            const label = headingMap[key];
            let value = row[key];
            if (typeof value === 'number' && index !== 0 && index !== 1) value = amountFormat(value);
            return [label, value];
        })
    ));
}
